import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useFormatPrice from "@/hooks/use-format-price";
import {
  ReturnRequestLineItemNewVariantDto,
  ReturnRequestLineItemVariantDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@babackapp/ui/components/hover-card";

type SimpleExchangeHoverCardProps = {
  initialVariant: ReturnRequestLineItemVariantDto;
  newVariant?: ReturnRequestLineItemNewVariantDto;
  originalPrice?: string;
  newVariantPrice?: number;
};

export function SimpleExchangeHoverCard({
  children,
  initialVariant,
  newVariant,
  originalPrice,
  newVariantPrice,
}: React.PropsWithChildren<SimpleExchangeHoverCardProps>) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const formatPrice = useFormatPrice();
  const currency = shop.preferences.currency;

  const priceComparison = (() => {
    if (originalPrice === undefined || newVariantPrice === undefined)
      return null;
    const oldAmount = parseFloat(originalPrice);
    if (isNaN(oldAmount)) return null;
    const delta = newVariantPrice - oldAmount;
    return {
      old: formatPrice({ amount: oldAmount, currency }),
      new: formatPrice({ amount: newVariantPrice, currency }),
      delta: `${delta > 0 ? "+" : ""}${formatPrice({ amount: delta, currency })}`,
      hasDiff: delta !== 0,
    };
  })();

  const newVariantLabel =
    [newVariant?.option1, newVariant?.option2, newVariant?.option3]
      .filter(Boolean)
      .join(", ") || newVariant?.productTitle;

  return (
    <>
      <span className="text-sm text-foreground font-medium">
        {newVariantLabel}
      </span>

      <HoverCard>
        <HoverCardTrigger>{children}</HoverCardTrigger>
        <HoverCardContent>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col">
              <span className="text-muted-foreground font-medium text-xs">
                {t("returnDetail.update.initialVariant")}
              </span>
              <p className="text-sm text-foreground font-medium">
                {initialVariant.title}
              </p>
            </div>
            <div className="flex flex-col">
              <span className="text-muted-foreground font-medium text-xs">
                {t("returnDetail.update.newVariant")}
              </span>
              <p className="text-sm text-foreground font-medium">
                {newVariantLabel}
              </p>
            </div>
            {priceComparison && (
              <div className="flex flex-col gap-1 border-t pt-2">
                <span className="text-muted-foreground font-medium text-xs">
                  {t("returnDetail.update.originalPrice")}
                </span>
                <p className="text-sm text-foreground font-medium">
                  {priceComparison.old} → {priceComparison.new}
                </p>
                {priceComparison.hasDiff && (
                  <p className="text-xs font-medium text-amber-600">
                    {priceComparison.delta}
                  </p>
                )}
              </div>
            )}
          </div>
        </HoverCardContent>
      </HoverCard>
    </>
  );
}
